// src/pages/ListarKitBlindagemHistorico.jsx
import React, { useEffect, useState, useMemo } from "react";
import {
  Box, Typography, Table, TableBody, TableCell, TableHead, TableRow, Paper, Button, TextField, MenuItem,
  Tooltip, Chip, Stack, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, Card, CardContent, Skeleton, InputAdornment, IconButton, TablePagination
} from "@mui/material";

import { FileDownload, Search, Today, Redeem, MonetizationOn, Delete, Block, TaskAlt } from "@mui/icons-material";
import {
  LineChart, Line, XAxis, YAxis, Tooltip as ChartTooltip, ResponsiveContainer,
  BarChart, Bar, Legend, CartesianGrid, Cell
} from "recharts";
import { alpha } from "@mui/material/styles";
import api from "../services/api";

import { Link } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import SearchUserAutocomplete from "../components/SearchUserAutocomplete";

// rota da licença
const toLicenca = (chave) => `/licencas/novo/${encodeURIComponent(chave)}`;

// Paleta padronizada
const statusVisual = {
  pendente:   { bg: "#e53935", fg: "#fff", label: "pendente" },
  pago:       { bg: "#43a047", fg: "#fff", label: "pago" },
  brinde:     { bg: "#039BE5", fg: "#fff", label: "brinde" },
  desativado: { bg: "#222",    fg: "#fff", label: "desativado" },
  confirmado: { bg: "#FFEB3B", fg: "#333", label: "confirmado" },
  credito:    { bg: "#7B1FA2", fg: "#fff", label: "crédito" },
};

const STATUS_KEYS = ["pendente", "pago", "brinde", "desativado", "confirmado", "credito"];
const PAGE_SIZES = [50, 100, 200, 500, 1000];

// aplica a cor da linha conforme o status
const rowSxByStatus = (status) => {
  const key = String(status || "").toLowerCase();
  const color = statusVisual?.[key]?.bg;
  if (!color) return {};
  return {
    background: `linear-gradient(90deg, ${alpha(color, 0.08)} 0%, ${alpha(color, 0.04)} 60%, transparent 100%)`,
    "&:hover": { backgroundColor: alpha(color, 0.12) },
    "& td, & th": { borderBottomColor: alpha(color, 0.25) },
    "& td:first-of-type": { borderLeft: `4px solid ${color}` }
  };
};

export default function ListarKitBlindagemHistorico() {
  const [registros, setRegistros] = useState([]);
  const [total, setTotal] = useState(0);

  const [filtros, setFiltros] = useState({ status: "", dataIni: "", dataFim: "", busca: "" });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showDetails, setShowDetails] = useState(null);
  const [graficoDias, setGraficoDias] = useState([]);
  const [graficoStatus, setGraficoStatus] = useState([]);

  // paginação
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);

  // Admin + busca digitável (reaproveitando componente)
  const isAdmin = (localStorage.getItem("tipo") === "admin");
  const [usuarioSel, setUsuarioSel] = useState(null); // { nome, chave }

  // Datas rápidas
  const setHoje = () => {
    const hoje = new Date();
    const yyyy = hoje.getFullYear();
    const mm = String(hoje.getMonth() + 1).padStart(2, "0");
    const dd = String(hoje.getDate()).padStart(2, "0");
    const d = `${yyyy}-${mm}-${dd}`;
    setFiltros((f) => ({ ...f, dataIni: d, dataFim: d }));
  };
  const setTrintaDias = () => {
    const hoje = new Date();
    const yyyy = hoje.getFullYear();
    const mm = String(hoje.getMonth() + 1).padStart(2, "0");
    const dd = String(hoje.getDate()).padStart(2, "0");
    const hojeStr = `${yyyy}-${mm}-${dd}`;
    const antes = new Date(); antes.setDate(hoje.getDate() - 29);
    const yyyyA = antes.getFullYear();
    const mmA = String(antes.getMonth() + 1).padStart(2, "0");
    const ddA = String(antes.getDate()).padStart(2, "0");
    const trintaDiasStr = `${yyyyA}-${mmA}-${ddA}`;
    setFiltros((f) => ({ ...f, dataIni: trintaDiasStr, dataFim: hojeStr }));
  };

  // Busca client-side (no resultado da página atual)
  const filtradas = useMemo(
    () =>
      registros.filter((r) => {
        if (!filtros.busca) return true;
        const q = filtros.busca.toLowerCase();
        const statusTxt = (statusVisual[r.status]?.label || r.status || "").toLowerCase();
        return (
          r.chave?.toLowerCase().includes(q) ||
          r.cliente_nome?.toLowerCase().includes(q) ||
          r.usuario_nome?.toLowerCase().includes(q) ||
          r.usuario_chave?.toLowerCase().includes(q) ||
          r.observacao?.toLowerCase().includes(q) ||
          statusTxt.includes(q) ||
          String(r.id || "").includes(q)
        );
      }),
    [registros, filtros.busca]
  );

  // Gráficos (com base na página atual — leve)
  function processaGraficoDias(data) {
    const mapa = {};
  
    data.forEach((r) => {
      if (!r?.data_lancamento) return;
  
      const dia = String(r.data_lancamento).slice(0, 10); // ✅ define dia aqui
  
      if (!mapa[dia]) {
        mapa[dia] = {
          dia,
          pendentes: 0,
          pagos: 0,
          brindes: 0,
          desativados: 0,
          confirmados: 0,
          creditos: 0,
          total: 0
        };
      }
  
      mapa[dia].total += 1;
  
      if (r.status === "pendente") mapa[dia].pendentes += 1;
      if (r.status === "pago") mapa[dia].pagos += 1;
      if (r.status === "brinde") mapa[dia].brindes += 1;
      if (r.status === "desativado") mapa[dia].desativados += 1;
      if (r.status === "confirmado") mapa[dia].confirmados += 1;
      if (r.status === "credito") mapa[dia].creditos += 1;
    });
  
    return Object.values(mapa).sort((a, b) => a.dia.localeCompare(b.dia));
  }
  
  function processaGraficoStatus(data) {
    const mapa = {};
    data.forEach((r) => {
      const k = r.status;
      if (!mapa[k]) mapa[k] = { status: k, quantidade: 0 };
      mapa[k].quantidade += 1;
    });
    return Object.values(mapa);
  }

  const fetchRegistros = async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        pageSize,
      };
      if (filtros.dataIni) params.data_ini = filtros.dataIni;
      if (filtros.dataFim) params.data_fim = filtros.dataFim;
      if (isAdmin && usuarioSel?.chave) params.usuario = usuarioSel.chave;
      if (filtros.status) params.status = filtros.status;

      const { data } = await api.get("/listar-kit-historico", { params });

      setRegistros(data.rows || []);
      setTotal(data.total || 0);

      setGraficoDias(processaGraficoDias(data.rows || []));
      setGraficoStatus(processaGraficoStatus(data.rows || []));
    } finally {
      setLoading(false);
    }
  };

  // carrega inicialmente
  useEffect(() => { fetchRegistros(); /* eslint-disable-next-line */ }, []);
  // quando trocar de revendedor no admin, recarrega
  useEffect(() => { if (isAdmin) { setPage(0); fetchRegistros(); } /* eslint-disable-next-line */ }, [usuarioSel]);
  // quando mudar página/tamanho, refetch
  useEffect(() => { fetchRegistros(); /* eslint-disable-next-line */ }, [page, pageSize]);

  const TZ = "America/Sao_Paulo";
  const csv = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

  function fmtDateTimeBR(v) {
    if (!v) return "—";
    const s = String(v);
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/);
    if (!m) return s;
    const [, ano, mes, dia, hh, mm, ss] = m;
    return `${dia}/${mes}/${ano} ${hh}:${mm}${ss ? `:${ss}` : ""}`;
  }

  function fmtDateBR(v) {
    if (!v) return "—";
    const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return v;
    const [, ano, mes, dia] = m;
    return `${dia}/${mes}/${ano}`;
  }

  const exportarCSV = () => {
    try {
      setExporting(true);
      const header = [
        "ID","Chave","Cliente","Status","Data Lançamento",
        "Data Confirmação","Data Vencimento","Usuário","Usuário Chave","Observação"
      ].join(";");
      const rows = registros.map((r) => ([
        r.id, r.chave, r.cliente_nome, r.status,
        fmtDateTimeBR(r.data_lancamento),
        fmtDateTimeBR(r.data_confirmacao),
        fmtDateBR(r.data_vencimento),
        r.usuario_nome || "", r.usuario_chave || "", r.observacao
      ].map(csv).join(";")));
      const csvStr = [header, ...rows].join("\n");
      const blob = new Blob([csvStr], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `kit_blindagem_p${page + 1}_n${pageSize}.csv`; a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const limparFiltros = () => {
    setFiltros({ status: "", dataIni: "", dataFim: "", busca: "" });
    setUsuarioSel(null);
    setPage(0);
    fetchRegistros();
  };

  const statusResumo = useMemo(() => {
    const mapa = {};
    registros.forEach((r) => {
      const k = r.status;
      if (!mapa[k]) mapa[k] = { quantidade: 0 };
      mapa[k].quantidade += 1;
    });
    return mapa;
  }, [registros]);

  // Ações por linha
  const alterarStatus = async (row, novoStatus) => {
    try {
      if (row.status === novoStatus) return;
      setLoading(true);
      await api.patch(`/${row.chave}/kit-historico/${row.id}`, { status: novoStatus });
      await fetchRegistros();
    } catch (e) {
      console.error("Erro ao alterar status:", e);
      alert("Não foi possível alterar este item.");
    } finally {
      setLoading(false);
    }
  };
  const excluirLinha = async (row) => {
    const ok = window.confirm(`Excluir o lançamento #${row.id}?`);
    if (!ok) return;
    try {
      setLoading(true);
      await api.delete(`/${row.chave}/kit-historico/${row.id}`);
      await fetchRegistros();
    } catch (e) {
      console.error("Erro ao excluir:", e);
      alert("Não foi possível excluir.");
    } finally {
      setLoading(false);
    }
  };

  const handleClickFiltrar = () => {
    setPage(0);
    fetchRegistros();
  };

  return (
    <Box p={2} maxWidth="1300px" mx="auto">
      <Typography variant="h5" mb={2} fontWeight={700}>Histórico Kit Blindagem</Typography>

      {/* Filtros */}
      <Paper sx={{ p: 1, mb: 2, width: "100%" }}>
        {/* LINHA 1 */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          {isAdmin && (
            <>
              <SearchUserAutocomplete
                value={usuarioSel}
                onChange={(u) => { setUsuarioSel(u); setPage(0); fetchRegistros(); }}
                sx={{ mr: 1 }}
              />
              <Button
                size="small"
                onClick={() => { setUsuarioSel(null); setPage(0); fetchRegistros(); }}
              >
                Ver todos
              </Button>
            </>
          )}

          <TextField
            label="Status"
            select
            size="small"
            value={filtros.status}
            onChange={(e) => setFiltros((f) => ({ ...f, status: e.target.value }))}
            sx={{ width: 130 }}
            InputProps={{ sx: { height: 34, fontSize: 14 } }}
          >
            <MenuItem value="">Todos</MenuItem>
            {STATUS_KEYS.map((k) => (
              <MenuItem key={k} value={k}>{statusVisual[k].label}</MenuItem>
            ))}
          </TextField>

          <TextField
            label="De"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={filtros.dataIni}
            onChange={(e) => setFiltros((f) => ({ ...f, dataIni: e.target.value }))}
            sx={{ width: 130 }}
            InputProps={{ sx: { height: 34, fontSize: 14 } }}
          />

          <TextField
            label="Até"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={filtros.dataFim}
            onChange={(e) => setFiltros((f) => ({ ...f, dataFim: e.target.value }))}
            sx={{ width: 130 }}
            InputProps={{ sx: { height: 34, fontSize: 14 } }}
          />

          <TextField
            label="Busca"
            size="small"
            placeholder="Nome, CHAVE, status, observação."
            value={filtros.busca}
            onChange={(e) => setFiltros((f) => ({ ...f, busca: e.target.value }))}
            sx={{ flex: "1 1 260px", minWidth: 260 }}
            InputProps={{
              sx: { height: 34, fontSize: 14 },
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* LINHA 2 */}
        <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button startIcon={<Today />} variant="outlined" size="small" onClick={setHoje} sx={{ px: 1, whiteSpace: "nowrap" }}>
              HOJE
            </Button>
            <Button startIcon={<Today />} variant="outlined" size="small" onClick={setTrintaDias} sx={{ px: 1, whiteSpace: "nowrap" }}>
              30 DIAS
            </Button>
          </Box>

          <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
            <Button variant="contained" size="small" onClick={handleClickFiltrar} disabled={loading} sx={{ px: 2, whiteSpace: "nowrap" }}>
              {loading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null} FILTRAR
            </Button>
            <Button variant="outlined" size="small" onClick={limparFiltros}>LIMPAR</Button>
            <Button startIcon={<FileDownload />} variant="outlined" size="small" onClick={exportarCSV} disabled={exporting}>
              {exporting ? "Exportando..." : "CSV (página)"}
            </Button>
          </Box>
        </Box>
      </Paper>

      <Grid container spacing={2} columns={{ xs: 4, sm: 8, md: 18 }} sx={{ mb: 2, alignItems: "stretch" }}>
        {STATUS_KEYS.map((k) => (
          <Grid item key={k} xs={4} sm={4} md={3}>
            <Card
              onClick={() => { setFiltros((f) => ({ ...f, status: k })); setPage(0); setTimeout(fetchRegistros, 0); }}
              sx={{
                cursor: "pointer",
                width: "100%",
                height: "100%",
                borderRadius: 2,
                boxShadow: 1,
                background: `linear-gradient(180deg, ${(statusVisual[k]?.bg || "#777")}22, #fff)`,
                borderTop: `5px solid ${statusVisual[k]?.bg || "#777"}`,
                transition: "transform .06s ease",
                "&:hover": { transform: "translateY(-2px)" },
              }}
            >
              <CardContent sx={{ p: 2 }}>
                <Typography variant="overline" sx={{ color: statusVisual[k]?.bg || "#777", fontWeight: 700 }}>
                  {statusVisual[k]?.label || k}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                  {statusResumo[k]?.quantidade || 0}
                </Typography>
                <Typography variant="caption" color="text.secondary">registros (página)</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Gráficos */}
      <Stack direction={{ xs: "column", md: "row" }} gap={2} mb={3}>
        <Paper sx={{ flex: 2, p: 2 }}>
          <Typography variant="subtitle2" mb={1} fontWeight={700}>Registros por Dia (página)</Typography>
          {loading ? (
            <Skeleton variant="rounded" height={200} />
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={graficoDias}>
                <XAxis dataKey="dia" fontSize={12} />
                <YAxis fontSize={12} allowDecimals={false} />
                <ChartTooltip
                  labelFormatter={(v) =>
                    new Intl.DateTimeFormat("pt-BR", { timeZone: TZ }).format(new Date(v))
                  }
                />
                <Line type="monotone" dataKey="pendentes"   name="pendentes"   stroke={statusVisual.pendente.bg}   strokeWidth={2} dot />
                <Line type="monotone" dataKey="pagos"       name="pagos"       stroke={statusVisual.pago.bg}       strokeWidth={2} dot />
                <Line type="monotone" dataKey="brindes"     name="brindes"     stroke={statusVisual.brinde.bg}     strokeWidth={2} dot />
                <Line type="monotone" dataKey="desativados" name="desativados" stroke={statusVisual.desativado.bg} strokeWidth={2} dot />
                <Line type="monotone" dataKey="confirmados" name="confirmados" stroke={statusVisual.confirmado.bg} strokeWidth={2} dot />
                <Line type="monotone" dataKey="creditos"    name="créditos"    stroke={statusVisual.credito.bg}    strokeWidth={2} dot />
                <CartesianGrid strokeDasharray="3 3" />
                <Legend />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Paper>

        <Paper sx={{ flex: 1, p: 2 }}>
          <Typography variant="subtitle2" mb={1} fontWeight={700}>Por Status (página)</Typography>
          {loading ? (
            <Skeleton variant="rounded" height={200} />
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart
                data={STATUS_KEYS.map((st) => ({
                  status: st,
                  quantidade: (graficoStatus.find((g) => g.status === st)?.quantidade) || 0,
                }))}
              >
                <XAxis dataKey="status" fontSize={12} tickFormatter={(s) => statusVisual[s]?.label || s} />
                <YAxis fontSize={12} allowDecimals={false} />
                <ChartTooltip />
                <Bar dataKey="quantidade" radius={[6, 6, 0, 0]}>
                  {STATUS_KEYS.map((st) => (
                    <Cell key={st} fill={statusVisual[st].bg} />
                  ))}
                </Bar>
                <Legend />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Paper>
      </Stack>

      {/* Tabela */}
      <Paper sx={{ overflowX: "auto" }}>
        <Table size="small" stickyHeader>
          <TableHead sx={{ bgcolor: "#fafafa" }}>
            <TableRow>
              <TableCell>Data Lançamento</TableCell>
              <TableCell>Chave</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Data Confirmação</TableCell>
              <TableCell>Data Vencimento</TableCell>
              <TableCell>Usuário</TableCell>
              <TableCell>Observação</TableCell>
              {isAdmin && <TableCell align="center">Ações</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              [...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: isAdmin ? 8 : 7 }).map((__, j) => (
                    <TableCell key={j}><Skeleton height={22} /></TableCell>
                  ))}
                </TableRow>
              ))
            )}
            {!loading && filtradas.length === 0 && (
              <TableRow>
                <TableCell colSpan={isAdmin ? 8 : 7} align="center" sx={{ color: "text.secondary", py: 4 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>Sem registros no período.</Typography>
                  <Button size="small" onClick={limparFiltros}>Limpar filtros</Button>
                </TableCell>
              </TableRow>
            )}
            {!loading && filtradas.map((r) => (
              <TableRow
                key={r.id}
                hover
                tabIndex={0}
                onClick={() => setShowDetails(r)}
                onKeyDown={(e) => { if (e.key === "Enter") setShowDetails(r); }}
                sx={{ cursor: "pointer", ...rowSxByStatus(r.status) }}
              >
                <TableCell>{fmtDateTimeBR(r.data_lancamento)}</TableCell>

                <TableCell onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                  <Tooltip title={r.chave || ""}>
                    <Box sx={{ maxWidth: 280 }}>
                      <Link
                        component={RouterLink}
                        to={toLicenca(r.chave)}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        sx={{ fontFamily: "ui-monospace, Menlo, monospace", fontWeight: 600 }}
                      >
                        {r.chave}
                      </Link>

                      {r.cliente_nome && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                          {r.cliente_nome}
                        </Typography>
                      )}
                    </Box>
                  </Tooltip>
                </TableCell>

                <TableCell>
                  <Chip
                    label={statusVisual[r.status]?.label || r.status}
                    size="small"
                    sx={{
                      bgcolor: statusVisual[r.status]?.bg || "#e0e0e0",
                      color: statusVisual[r.status]?.fg || "#333",
                      fontWeight: 700
                    }}
                  />
                </TableCell>

                <TableCell>{fmtDateTimeBR(r.data_confirmacao)}</TableCell>
                <TableCell>{fmtDateBR(r.data_vencimento)}</TableCell>

                <TableCell>
                  {r.usuario_nome ? (
                    <Box sx={{ maxWidth: 260 }}>
                      <Typography variant="body2">{r.usuario_nome}</Typography>
                      {r.usuario_chave && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                          {r.usuario_chave}
                        </Typography>
                      )}
                    </Box>
                  ) : "—"}
                </TableCell>

                <TableCell>
                  <Tooltip title={r.observacao || ""}>
                    <span style={{ display: "inline-block", maxWidth: 380, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {r.observacao || "-"}
                    </span>
                  </Tooltip>
                </TableCell>

                {isAdmin && (
                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="Marcar como Pago">
                      <span>
                        <IconButton
                          size="small"
                          sx={{ color: statusVisual.pago.bg }}
                          disabled={r.status === "pago"}
                          onClick={(e) => { e.stopPropagation(); alterarStatus(r, "pago"); }}
                        >
                          <MonetizationOn fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Tooltip title="Marcar como Brinde">
                      <span>
                        <IconButton
                          size="small"
                          sx={{ color: statusVisual.brinde.bg }}
                          disabled={r.status === "brinde"}
                          onClick={(e) => { e.stopPropagation(); alterarStatus(r, "brinde"); }}
                        >
                          <Redeem fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Tooltip title="Desativar">
                      <span>
                        <IconButton
                          size="small"
                          sx={{ color: statusVisual.desativado.bg }}
                          disabled={r.status === "desativado"}
                          onClick={(e) => { e.stopPropagation(); alterarStatus(r, "desativado"); }}
                        >
                          <Block fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Tooltip title="Marcar como Confirmado">
                      <span>
                        <IconButton
                          size="small"
                          sx={{ color: statusVisual.confirmado.bg }}
                          disabled={r.status === "confirmado"}
                          onClick={(e) => { e.stopPropagation(); alterarStatus(r, "confirmado"); }}
                        >
                          <TaskAlt fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Tooltip title="Excluir">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => { e.stopPropagation(); excluirLinha(r); }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Paginação */}
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={PAGE_SIZES}
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          labelRowsPerPage="Linhas por página:"
        />
      </Paper>

      {/* Modal detalhes */}
      <Dialog open={!!showDetails} onClose={() => setShowDetails(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Detalhes do Registro</DialogTitle>
        <DialogContent dividers>
          {showDetails && (
            <Box display="grid" gridTemplateColumns="160px 1fr" rowGap={1} columnGap={2}>
              <Typography variant="body2" color="text.secondary">Chave</Typography>
              <Typography variant="body2">{showDetails.chave}</Typography>

              <Typography variant="body2" color="text.secondary">Status</Typography>
              <Typography variant="body2">
                {statusVisual[showDetails.status]?.label || showDetails.status}
              </Typography>

              <Typography variant="body2" color="text.secondary">Data Lançamento</Typography>
              <Typography variant="body2">{fmtDateTimeBR(showDetails.data_lancamento)}</Typography>

              <Typography variant="body2" color="text.secondary">Data Confirmação</Typography>
              <Typography variant="body2">{fmtDateTimeBR(showDetails.data_confirmacao)}</Typography>

              <Typography variant="body2" color="text.secondary">Data Vencimento</Typography>
              <Typography variant="body2">{fmtDateBR(showDetails.data_vencimento)}</Typography>

              <Typography variant="body2" color="text.secondary">Usuário</Typography>
              <Typography variant="body2">{showDetails.usuario_nome || "-"}</Typography>

              <Typography variant="body2" color="text.secondary">Observação</Typography>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{showDetails.observacao || "-"}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDetails(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
